import type { NonUndefined, UnionToIntersection, ValuesType } from 'utility-types'

export type ExcludeValues<T, E> = Readonly<{ [P in keyof T]: Exclude<T[P], E> }>

export type NullableValues<T, K extends keyof T = keyof T> = Omit<T, K> & Readonly<{ [P in keyof T & K]: T[P] | null }>

export type NonNullableValues<T, K extends keyof T = keyof T> = Omit<T, K> & Readonly<{ [P in keyof T & K]: NonNullable<T[P]> }>

export type ArrayType<T extends Array<unknown> | readonly unknown[]> = T extends Array<infer U> ? U : T extends readonly (infer U)[] ? U : never

export type NonEmptyArray<T> = ([T, ...T[]] | [...T[], T]) & T[]

export type ReadonlyNonEmptyArray<T> = (readonly [T, ...T[]] | readonly [...T[], T]) & readonly T[]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ReadonlyRecord<K extends keyof any, T> = Readonly<Record<K, T>>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PartialRecord<K extends keyof any, T> = Partial<Record<K, T>>

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type PartialReadonlyRecord<K extends keyof any, T> = Partial<ReadonlyRecord<K, T>>

export type AnyFunction = (...args: any) => any

export type Is<A, B, Then, Else> = [A] extends [B] ? Then : Else

/**
 * This type extracts all possible keys of a Union
 *
 * It works based on UnionToIntersection, but since
 * UnionToIntersection does not allow for mismatched unions to become
 * an impossible intersection (so we can extract all the keys all of it)
 * we first then need to make all of the keys to have a common type.
 *
 * The easiest to do it is to use Partial
 *
 * @see https://github.com/piotrwitek/utility-types/issues/192
 */
export type AllUnionKeys<U> = keyof UnionToIntersection<Partial<U>>

/**
 * The NonUndefined type is used here to handle properties that are present, but optional
 * If they are retrieved, the undefined needs to go as it means nothing to the output, and should be a never instead
 */
export type SubProperty<T, P extends keyof any> = NonUndefined<T extends PartialRecord<P, unknown> ? T[P] : never>

type UnionToOverloadedFunctions<U> = UnionToIntersection<U extends any ? (f: U) => void : never>
type LastUnionMember<U> = UnionToOverloadedFunctions<U> extends (a: infer A) => void ? A : never
type IsUnion<T> = Is<T, UnionToIntersection<T>, false, true>
type UnionToArray<T, A extends unknown[] = []> = IsUnion<T> extends true ? UnionToArray<Exclude<T, LastUnionMember<T>>, [LastUnionMember<T>, ...A]> : [T, ...A]
type Detupple<T> = { [P in keyof T]: T[P] extends [any, infer Type] ? Type : never }
type Tupple<T> = { [P in keyof T]: [P, T[P]] }
/**
 * Turns the given type into a tupple of the given properties
 * @example { id: string, age: number, name: string } -> [string, number, string]
 */
export type ArrayfyValues<T> = T extends never ? never : Detupple<UnionToArray<ValuesType<Tupple<T>>>>

export type Entries<T extends object> = (readonly [keyof T, ValuesType<T>])[]

/**
 * @see {Date}
 */
export type ReadonlyDate = Readonly<{
    /**
     * @see {Date.prototype.getTime}
     */
    getTime(): number

    /**
     * @see {Date.prototype.toUTCString}
     */
    toUTCString(): string

    /**
     * @see {Date.prototype.toISOString}
     */
    toISOString(): string
}>
